import {
	AddCircleOutlineOutlined,
	ChatOutlined,
	HelpOutlineOutlined,
	LayersOutlined,
	LogoutOutlined,
	SettingsOutlined,
	TourOutlined,
} from '@mui/icons-material';
import {
	Avatar,
	Box,
	Chip,
	Divider,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import Routes from '../../constants/routes.js';
import { track } from '../../utils/analytics';
import { stringAvatar } from '../../utils/formatter';
import LocaleSelector from '../../utils/localisation/locale-selector';

function SettingsMenu({ anchorEl, handleClose, user, liveHidden, postLogout }) {
	const { formatMessage } = useIntl();
	const navigate = useNavigate();

	const { organisation, fullname, isAdmin, isSuperAdmin, toursEnabled } = user;

	const userRole = isAdmin ? 'teamAdmin' : isSuperAdmin ? 'superAdmin' : 'user';

	return (
		<Menu
			anchorEl={anchorEl}
			open={Boolean(anchorEl)}
			onClose={handleClose}
			disablePortal
			keepMounted
			sx={{
				li: {
					padding: '0.7em 1.5em',
				},
			}}
			slotProps={{
				paper: {
					id: 'account-menu',
					elevation: 0,
					sx: {
						overflow: 'visible',
						filter: 'drop-shadow(-6px 0 10px #312c5d24)',
						borderRadius: '10px',
						boxShadow: 'unset',
						mt: '15px',
						minWidth: '300px',
						'& .MuiAvatar-root': {
							width: 45,
							height: 45,
							ml: -0.5,
							mr: 2,
						},
						'&:before': {
							content: '""',
							display: 'block',
							position: 'absolute',
							top: 0,
							right: 14,
							width: 10,
							height: 10,
							bgcolor: 'background.paper',
							transform: 'translateY(-50%) rotate(45deg)',
							zIndex: 0,
						},
					},
				},
			}}
			transformOrigin={{ horizontal: 'right', vertical: 'top' }}
			anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
		>
			<MenuItem
				onClick={() => {
					handleClose();
					navigate(Routes.settings.index);
				}}
			>
				<ListItemIcon>
					<Avatar {...stringAvatar(fullname)} />
				</ListItemIcon>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<ListItemText
						disableTypography
						primary={
							<Typography fontSize={15} variant="subtitle2" lineHeight={1.2} noWrap>
								{organisation}
							</Typography>
						}
					/>
					<ListItemText
						disableTypography
						primary={
							<Typography fontSize={14} variant="body1" noWrap>
								{fullname}
							</Typography>
						}
						secondary={
							<Typography variant="body2" fontSize={14} color="text.secondary">
								{formatMessage({ id: `Header.account.role.${userRole}` })}
							</Typography>
						}
					/>
				</Box>
			</MenuItem>
			{/* {toursEnabled && (
				<MenuItem
					id="navigation-interactive-tours"
					onClick={() => {
						track('Clicked Interactive Tours');
						handleClose();
						navigate(Routes.settings.helpCentre);
					}}
				>
					<ListItemIcon>
						<TourOutlined fontSize="small" />
					</ListItemIcon>
					{formatMessage({ id: 'Header.interactiveTours' })}
				</MenuItem>
			)} */}
			{isSuperAdmin && (
				<>
					<Divider />
					<MenuItem
						id="navigation-buyCredits"
						key="navigation-buyCredits"
						onClick={() => {
							handleClose();
							track('Navigate to Buy Credits page [Account Menu]');
							navigate(Routes.settings.buyCredits);
						}}
					>
						<ListItemIcon>
							<AddCircleOutlineOutlined fontSize="small" />
						</ListItemIcon>
						{formatMessage({ id: 'Header.admin.buyCredits' })}
					</MenuItem>
				</>
			)}

			{isSuperAdmin && !liveHidden && (
				<MenuItem
					id="navigation-subscription"
					key="navigation-subscription"
					onClick={() => {
						handleClose();
						track('Navigate to Subscription page [Account Menu]');
						navigate(Routes.settings.subscription);
					}}
				>
					<ListItemIcon>
						<LayersOutlined fontSize="small" />
					</ListItemIcon>
					{formatMessage({ id: 'Header.admin.subscription' })}
				</MenuItem>
			)}
			<Divider />
			<MenuItem
				id="navigation-help"
				onClick={() => {
					handleClose();
					track('Navigate to Help page [Account Menu]');
					navigate(Routes.settings.helpCentre);
				}}
				title={formatMessage({ id: 'Header.helpCentre.title' })}
			>
				<ListItemIcon>
					<HelpOutlineOutlined fontSize="small" />
				</ListItemIcon>
				{formatMessage({ id: 'Header.helpCentre.title' })}
			</MenuItem>
			<MenuItem
				id="navigation-feedback"
				aria-label="Feedback"
				onClick={() => {
					track('Clicked Feedback');
					handleClose();
					window.postMessage({
						target: 'FeaturebaseWidget',
						data: {
							action: 'openFeedbackWidget',
							//   setBoard: 'yourboardname', // optional - preselect a board
						},
					});
				}}
			>
				<ListItemIcon>
					<ChatOutlined fontSize="small" />
				</ListItemIcon>
				{formatMessage({ id: 'Header.feedback' })}
			</MenuItem>
			<MenuItem
				id="navigation-settings"
				onClick={() => {
					track('Navigate to Settings [Account Menu]');
					handleClose();
					navigate(Routes.settings.index);
				}}
			>
				<ListItemIcon>
					<SettingsOutlined fontSize="small" />
				</ListItemIcon>
				{formatMessage({ id: 'Header.account.settings' })}
			</MenuItem>
			<MenuItem sx={{ padding: '0.3em 0.7em !important' }}>
				<LocaleSelector
					size="small"
					reloadPageOnChange
					sx={{
						border: 'none',
						width: '100%',
						boxShadow: 'none',
						'.MuiOutlinedInput-notchedOutline': { border: 0 },
					}}
				/>
			</MenuItem>
			<Divider />
			<MenuItem
				id="navigation-logout"
				onClick={() => {
					handleClose();
					postLogout();
				}}
			>
				<ListItemIcon>
					<LogoutOutlined fontSize="small" />
				</ListItemIcon>
				{formatMessage({ id: 'Header.account.logout' })}
			</MenuItem>
		</Menu>
	);
}

export default SettingsMenu;
